import { useEffect, useState } from 'react';
import { IconRefresh } from './icons.js';
import { OUT_OF_CREDITS_REASON } from './hooks/useServerSessionEnded.js';
import { isRechargeOffered } from '../../billing/rechargeAvailability.js';

interface SessionEndedCardProps {
  /**
   * Reason del cierre server-side: `idle-no-speech` (watch de inactividad),
   * `insufficient_credits` (corte por saldo) u otros → copy genérico.
   */
  reason: string;
  /**
   * Continúa la MISMA charla: remonta la pantalla con `resumeSessionId` de la
   * sesión cerrada → flujo "Continuar" completo (historial + contexto LLM,
   * SIN opener nuevo — habla el usuario).
   */
  onResume: () => void;
  onLeave: () => void;
}

/**
 * Card TERMINAL del cierre server-side de la sesión (`session.ended`, hoy el
 * watch idle-no-speech del backend). Se renderiza al FINAL del hilo de
 * mensajes (decisión del dueño: el cierre es parte de la conversación, no un
 * banner de sistema arriba). Reusa los estilos de `.conn-banner-lost` con un
 * modificador que lo asienta en el flujo del chat.
 */
/**
 * PREAVISO del cierre por inactividad (`session.closing`, 30s antes): card de
 * countdown al final del hilo — "decí algo para seguir". El contador vive acá
 * adentro (interval local) para que el tick por segundo no re-renderice la
 * pantalla entera. Desaparece si el user habla (el server manda
 * `session.closing.cancelled`) o cuando el cierre llega (la reemplaza
 * `SessionEndedCard`).
 */
export function SessionClosingCard({ inSeconds }: { inSeconds: number }) {
  const [left, setLeft] = useState(inSeconds);
  useEffect(() => {
    const timer = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1_000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="conn-banner conn-banner-retry session-closing-card" role="status" aria-live="polite">
      <span className="session-closing-count" aria-hidden="true">
        {left}
      </span>
      <span>
        La charla se va a pausar por inactividad en <strong>{left}s</strong> — decí algo (o
        escribí) para seguir.
      </span>
    </div>
  );
}

export function SessionEndedCard({ reason, onResume, onLeave }: SessionEndedCardProps) {
  const idle = reason === 'idle-no-speech';
  // Corte por saldo: NO ofrecemos "Retomar" — sin orbs el preflight rebota el
  // reintento y el botón sería una promesa falsa. El único camino honesto es
  // cargar orbs (la recarga vive en Inicio, como el resto del wallet). Where the
  // app does not sell orbs (native build without its store key) the copy only
  // says what happened: no pointer to a recharge that is not there.
  const outOfCredits = reason === OUT_OF_CREDITS_REASON;
  return (
    <div className="conn-banner conn-banner-lost session-ended-card" role="alert">
      <div className="conn-lost-text">
        <strong>
          {outOfCredits
            ? 'Te quedaste sin orbs'
            : idle
              ? 'La charla se pausó por inactividad'
              : 'La conversación terminó'}
        </strong>
        <span>
          {outOfCredits
            ? isRechargeOffered()
              ? 'La charla con el coach usa orbs y tu saldo se terminó. Cargá orbs desde Inicio y volvé cuando quieras — lo que hablaste hasta acá quedó guardado.'
              : 'La charla con el coach usa orbs y tu saldo se terminó. Lo que hablaste hasta acá quedó guardado.'
            : idle
              ? 'Retomala cuando quieras y seguí donde estabas — al volver, te toca hablar a vos.'
              : 'Podés retomarla cuando quieras y seguir donde estaba.'}
        </span>
      </div>
      <div className="conn-lost-actions">
        {outOfCredits ? null : (
          <button type="button" className="conn-btn conn-btn-primary" onClick={onResume}>
            <IconRefresh size={14} /> Retomar la charla
          </button>
        )}
        <button
          type="button"
          className={`conn-btn ${outOfCredits ? 'conn-btn-primary' : 'conn-btn-ghost'}`}
          onClick={onLeave}
        >
          Salir
        </button>
      </div>
    </div>
  );
}
